import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaArrowLeft } from 'react-icons/fa';
import SamanthaPhoto from '../assets/samantha-photo.jpg'; // Update path if needed
import Header from '../components/Header';
import Footer from '../components/Footer';

const PersonalJourney = () => {
  return (
    <>
      <Header />
      <NavContainer>
        <div className="container">
          <BackToHomeLink href="/">
            <FaArrowLeft /> Back to Home
          </BackToHomeLink>
        </div>
      </NavContainer>
      
      <JourneyContainer>
        <PageContent>
          <PageHeader
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <PageTitle>My Personal Journey</PageTitle>
            <PageDivider />
          </PageHeader>
          
          <ContentWrapper>
            <TextContent
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <JourneySection>
                <p>I was brought up and educated in a working-class family in the Northwest of England. I worked hard. I enjoyed time alone with my thoughts. I didn't step in to uncomfortable moments to share my opinions or to open up conversations which might require my input. I was a timid, a more observing middle child who lived in my head and felt the outside world was looking at me through judging eyes. I pulled the shutters down and sealed up. My myopic lens was my comfortable space.</p>
              </JourneySection>
              
              <JourneySection>
                <p>I moved through the decades sporting big hair, bolero jackets and tucker boots. I played tennis, competed, won and lost. I lost so many more games than I won …and that felt bad.</p>
              </JourneySection>
              
              <JourneySection>
                <p>I moved to London to train and qualify as a solicitor. I enjoyed the rigour of law yet knew that being an orator (I was a commercial litigator and in court) wasn't me. After 3 years, I moved into executive legal search. And this is where I have found my happy place of work.</p>
              </JourneySection>
              
              <JourneySection>
                <p>My two children, my loved ones and close friends are my constant sources of inspiration. I try to live my values; trust, family, health, love. I believe I am enough. I believe I can be of service to others. I believe that the more love I give away the more I receive.</p>
              </JourneySection>
              
              <JourneySection>
                <p>I do life… and life threw me some curve balls I didn't feel equipped to deal with. Life always does and knowing this, helped me move forward with kindness and compassion, firstly for myself and secondly for my children. I got hungry for "why" and immersed myself in educational books and podcasts about having the courage to be vulnerable, about recognising you need to go through moments of challenge to find peace and feeling comforted I was not going through his alone; there is a community out there who had gone through similar experiences. I needed to know I wasn't going crazy in this head of mine.</p>
              </JourneySection>
              
              <JourneySection>
                <p>Literally one Sunday afternoon, sprawled on the sofa, airpods in, personal development coaching came into my life. I was hooked. I could listen to, read and relate to everything I was hearing. You know what they say "the universe has a timing for you" and "what is meant for you will not pass you by" Oh, how true.</p>
              </JourneySection>
              
              <JourneySection>
                <p>Why am I sharing this? I want to share the strength of being vulnerable, of being open to reframing thoughts in a different way, of being able to tap into your skills and life experiences to compassionately move you to the next moment in your life. Life is a series of moments to cherish, embrace, show gratitude for and to learn from. I no longer ask "why did life happen to me in this way". I ask "what do I learn from why this happened".</p>
              </JourneySection>
              
              <JourneySection>
                <p>Coaching is one of the most positive self-reflective processes I have come across. To move your thinking from today, to tomorrow, with all that involves. It sounds simple and it is and yet, the results are far from simple. It is a transformative and enlightened process if you engage in it and believe you can.</p>
              </JourneySection>
              
              <JourneySection>
                <p>As a certified development coach, I am here to guide you in your journey and to congratulate you at each step of the way. Coaching charts your own personal progress. I applaud you for taking this first step in your journey of change.</p>
              </JourneySection>
            </TextContent>
            
            <SideContent
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <SideImageContainer>
                <img src={SamanthaPhoto} alt="Samantha Knowles" />
                <ImageBorder />
                <ImageDecoration className="decoration-1" />
                <ImageDecoration className="decoration-2" />
              </SideImageContainer>
              
              <QuoteBox>
                <QuoteText>
                  Life reshapes us in silence – every challenge, a chisel; every change, a mirror. Transformation doesn't ask for permission; it simply arrives, unfolding who we were meant to become.
                </QuoteText>
                <QuoteAuthor>- Rimi</QuoteAuthor>
              </QuoteBox>
            </SideContent>
          </ContentWrapper>
        </PageContent>
        
        <BackgroundElement className="element-1" />
        <BackgroundElement className="element-2" />
        <BackgroundElement className="element-3" />
      </JourneyContainer>
      <Footer />
    </>
  );
};

const NavContainer = styled.div`
  padding: 15px 0;
  background-color: #f9f9f9;
  box-shadow: 0 2px 5px rgba(0,0,0,0.05);
  margin-top: 80px; /* Space for fixed header */
  
  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 30px;
    display: flex;
  }
`;

const BackToHomeLink = styled.a`
  display: flex;
  align-items: center;
  color: #5A7F8C;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
  
  svg {
    margin-right: 8px;
    font-size: 0.9rem;
  }
  
  &:hover {
    color: #A8C4A0;
  }
`;

const JourneyContainer = styled.div`
  position: relative;
  background-color: white;
  min-height: 100vh;
  padding: 60px 0 80px;
  overflow: hidden;
`;

const PageContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 30px;
  position: relative;
  z-index: 2;
`;

const PageHeader = styled(motion.div)`
  text-align: center;
  margin-bottom: 60px;
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  color: #1A2A3A;
  margin-bottom: 25px;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const PageDivider = styled.div`
  width: 80px;
  height: 3px;
  background-color: #A8C4A0;
  margin: 0 auto;
`;

const ContentWrapper = styled.div`
  display: flex;
  gap: 60px;
  
  @media (max-width: 992px) {
    flex-direction: column;
  }
`;

const TextContent = styled(motion.div)`
  flex: 0.65;
  
  @media (max-width: 992px) {
    order: 2;
  }
`;

const JourneySection = styled.div`
  margin-bottom: 30px;
  
  p {
    font-size: 1.05rem;
    line-height: 1.8;
    color: #444;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SideContent = styled(motion.div)`
  flex: 0.35;
  
  @media (max-width: 992px) {
    order: 1;
    margin-bottom: 40px;
  }
`;

const SideImageContainer = styled.div`
  position: relative;
  margin-bottom: 40px;
  border-radius: 12px;
  overflow: hidden;
  width: 100%;
  max-width: 380px;
  
  @media (min-width: 1200px) {
    max-width: 350px; /* Prevent image from getting too large on big screens */
  }
  
  /* Ensure image isn't too large on wide screens */
  @media (min-width: 1600px) {
    max-width: 320px;
  }
  
  img {
    width: 100%;
    height: auto;
    max-height: 500px;
    display: block;
    border-radius: 12px;
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
    object-fit: cover;
  }
`;

const ImageBorder = styled.div`
  position: absolute;
  top: -15px;
  left: -15px;
  width: calc(100% - 30px);
  height: calc(100% - 30px);
  border: 2px solid #A8C4A0;
  border-radius: 12px;
  z-index: -1;
`;

const ImageDecoration = styled.div`
  position: absolute;
  border-radius: 50%;
  
  &.decoration-1 {
    width: 150px;
    height: 150px;
    background-color: rgba(168, 196, 160, 0.1);
    top: -40px;
    right: -40px;
    z-index: -1;
  }
  
  &.decoration-2 {
    width: 100px;
    height: 100px;
    background-color: rgba(90, 127, 140, 0.1);
    bottom: -30px;
    left: -30px;
    z-index: -1;
  }
`;

const QuoteBox = styled.div`
  background-color: #f9f9f9;
  border-left: 4px solid #A8C4A0;
  padding: 25px;
  border-radius: 8px;
  margin-bottom: 40px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05);
`;

const QuoteText = styled.p`
  font-style: italic;
  color: #1A2A3A;
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 15px;
`;

const QuoteAuthor = styled.p`
  text-align: right;
  color: #5A7F8C;
  font-weight: 500;
  margin: 0;
`;

const BackgroundElement = styled.div`
  position: absolute;
  z-index: 1;
  
  &.element-1 {
    width: 600px;
    height: 600px;
    background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v50H50v50H0z' fill='%23A8C4A0' fill-opacity='0.05'/%3E%3C/svg%3E");
    top: -100px;
    right: -200px;
    transform: rotate(-15deg);
  }
  
  &.element-2 {
    width: 500px;
    height: 500px;
    background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h50v50H0z' fill='%235A7F8C' fill-opacity='0.03'/%3E%3C/svg%3E");
    bottom: 100px;
    left: -150px;
    transform: rotate(10deg);
  }
  
  &.element-3 {
    width: 400px;
    height: 400px;
    background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v50H50v50H0z' fill='%23A8C4A0' fill-opacity='0.05'/%3E%3C/svg%3E");
    bottom: -150px;
    right: 10%;
    transform: rotate(20deg);
  }
`;

export default PersonalJourney;